import React, { useState, useEffect, useRef } from "react"
import Popup from "reactjs-popup"
import { TiCancel } from "react-icons/ti"
import { FaUserMinus } from "react-icons/fa"
import { IoClose } from "react-icons/io5"
import { useDispatch, useSelector } from "react-redux"
import { getNursePatients } from "../../../features/schedule/derivedAssignments"
import {
	removeNurse,
	setOptimizeData,
	setIsEditing,
	transferPatientsBetweenNursesAndChairs,
	markNurseAsEdited,
	updateSchedule
} from "../../../features/schedule/scheduleSlice"
import {
	removeTomorrowNurse,
	setTomorrowData,
	transferPatientsBetweenNursesAndChairsTomorrow,
	markNurseAsEditedTomorrow,
	updateTomorrowSchedule
} from "../../../features/schedule/tomorrowSlice"
import {
	removeSandboxNurse,
	transferPatientsBetweenNursesAndChairsSandbox,
	updateSandboxSchedule
} from "../../../features/schedule/sandboxSlice"
import ConfirmationDialog from "./ConfirmationDialog"
import { useAuth0 } from "@auth0/auth0-react"
import store from "../../../store"
import {
	getDateFormatted,
	getFirstOpenDateTomorrow,
	getFirstOpenDateToday,
	getCurrentTime
} from "../../../utils/generalUtils"
import { getAssignmentsForNurseRemoval } from "../../../hooks/usePatientReassignments"
import PatientReassignmentConfirmation from "./PatientReassignmentConfirmation"
import { getScheduleSelection } from "./scheduleSelection"
import {
	currentlyNurseRealTimeOngoing,
	currentlyNurseRealTimeFuture
} from "../../../utils/realTimeNurseUtils"
import { getScheduleFromMongo } from "../../../utils/mongoUtils"
import { useSchedulePatch } from "../../../hooks/useSchedulePatch"
import { confirmedCompletedAllCases } from "../../../utils/realTimeUtils"
import useLogging from "../../../hooks/useLogging"
import useFetchSettings from "../../../hooks/useFetchSettings"
import _ from "lodash"

const RemoveNursePopup = ({ setNotification }) => {
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })

	const dispatch = useDispatch()
	const { user, getAccessTokenSilently } = useAuth0() // Use Auth0 for getting the access token
	const { settingsData } = useFetchSettings()
	const [editedProperties, setEditedProperties] = useState([])

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const logging = useLogging()

	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)
	const date = isTomorrowMode
		? nextDate
		: getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates))

	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [selectedNurse, setSelectedNurse] = useState(null)

	const [isTransitionAcceptOpen, setIsTransitionAcceptOpen] = useState(false)
	const [reassignments, setReassignments] = useState([])

	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)

	const currentTime = getCurrentTime(sandbox, isTomorrowMode)

	const handlePopupOpen = () => {
		logging.info("RemoveNursePopup - handlePopupOpen", {
			component: "RemoveNursePopup",
			function: "handlePopupOpen"
		})
		if (scrollableRef.current) {
			scrollableRef.current.scrollTop = 0
		}
	}

	// Regular close handler for escape/click-outside
	const handleClose = () => {
		logging.info("RemoveNursePopup - handleClose", {
			component: "RemoveNursePopup",
			function: "handleClose",
			nurseToRemove: selectedNurse
		})
		dispatch(setIsEditing(false))
	}

	const handleRemoveNurse = async nurse => {
		logging.info("RemoveNursePopup - handleRemoveNurse", {
			component: "RemoveNursePopup",
			function: "handleRemoveNurse",
			nurseToRemove: nurse
		})
		dispatch(setIsEditing(true))

		const currentState = isTomorrowMode
			? store.getState().tomorrow
			: sandbox
				? store.getState().sandbox
				: store.getState().schedule

		// Get a fresh merged schedule from the backend (Pull)
		try {
			let newSchedule = null

			if (!sandbox) {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})

				newSchedule = await getScheduleFromMongo(
					token,
					date,
					dispatch,
					currentState,
					isTomorrowMode,
					sandbox,
					setTomorrowData,
					setOptimizeData,
					logging
				)
			} else {
				newSchedule = currentState
			}

			// Verify newSchedule has required properties
			if (!newSchedule || !newSchedule.nurseSchedule) {
				logging.error("RemoveNursePopup - newSchedule or nurseSchedule is undefined", {
					component: "RemoveNursePopup",
					function: "handleRemoveNurse",
					newSchedule: newSchedule
				})
				dispatch(setIsEditing(false))
				return
			}

			const nurseToRemove = newSchedule.nurseSchedule.find(n => n.nurseId === nurse.nurseId)

			if (!nurseToRemove) {
				logging.error("RemoveNursePopup - nurseToRemove not found", {
					component: "RemoveNursePopup",
					function: "handleRemoveNurse",
					nurseToRemove: nurse
				})

				if (isTomorrowMode) {
					dispatch(setTomorrowData(newSchedule))
				} else if (sandbox) {
					dispatch(updateSandboxSchedule(newSchedule))
				} else {
					dispatch(setOptimizeData(newSchedule))
				}
				dispatch(setIsEditing(false))
				return
			}

			await handlePatientReassignment(newSchedule, nurseToRemove)
		} catch (error) {
			logging.error("RemoveNursePopup - error fetching merged schedule:", {
				error: error.message,
				stack: error.stack
			})
			dispatch(setIsEditing(false))
		}
	}

	const handlePatientReassignment = async (schedule, nurseToRemove) => {
		const reassignments = getAssignmentsForNurseRemoval(
			nurseToRemove,
			schedule,
			false,
			currentTime,
			1440,
			settingsData,
			sandbox,
			isTomorrowMode
		)

		setSelectedNurse(nurseToRemove)

		if (reassignments && reassignments.length > 0) {
			logging.info("RemoveNursePopup - there are reassignments", {
				component: "RemoveNursePopup",
				function: "handlePatientReassignment",
				nurseToRemove: nurseToRemove,
				reassignments: reassignments
			})
			setReassignments(reassignments)
			setIsTransitionAcceptOpen(true)
		} else {
			logging.info("RemoveNursePopup - no reassignments", {
				component: "RemoveNursePopup",
				function: "handlePatientReassignment",
				nurseToRemove: nurseToRemove
			})
			setIsDialogOpen(true)
		}
	}

	const updateLunchBreakTimes = (nurse, currentMinutes) => {
		let updatedLunchBreakStart = nurse.scheduledLunchBreakStart
		let updatedLunchBreakEnd = nurse.scheduledLunchBreakEnd

		if (currentMinutes < nurse.scheduledLunchBreakStart) {
			updatedLunchBreakStart = null
			updatedLunchBreakEnd = null
		} else if (currentMinutes < nurse.scheduledLunchBreakEnd) {
			updatedLunchBreakEnd = currentMinutes
		}

		return {
			updatedLunchBreakStart,
			updatedLunchBreakEnd
		}
	}

	const editNurseInfo = () => {
		const currentState = isTomorrowMode
			? store.getState().tomorrow
			: sandbox
				? store.getState().sandbox
				: store.getState().schedule

		const editedNurseId = selectedNurse.nurseId

		const existingNurse = currentState.nurseSchedule.find(n => n.nurseId === editedNurseId)

		const { updatedLunchBreakStart, updatedLunchBreakEnd } = updateLunchBreakTimes(
			selectedNurse,
			currentTime
		)

		const updatedNurse = {
			...existingNurse,
			scheduledEndTime: Math.max(currentTime, existingNurse.scheduledStartTime),
			scheduledLunchBreakStart: updatedLunchBreakStart,
			scheduledLunchBreakEnd: updatedLunchBreakEnd
		}

		setEditedProperties(prev => [
			...prev,
			"scheduledEndTime",
			"scheduledLunchBreakStart",
			"scheduledLunchBreakEnd"
		])

		// Update nurse schedule
		let updatedNurseSchedule = currentState.nurseSchedule.map(n =>
			n.nurseId === editedNurseId ? updatedNurse : n
		)

		let updatedPatientSchedule = _.cloneDeep(currentState.patientSchedule)
		let updatedChairSchedule = _.cloneDeep(currentState.chairSchedule)

		const updatedNursePatients = getNursePatients(updatedPatientSchedule, updatedNurse.nurseId)
		const completelyRemoveNurse = updatedNursePatients.every(
			patient => !confirmedCompletedAllCases(patient, sandbox, isTomorrowMode)
		)

		if (completelyRemoveNurse) {
			updatedNurseSchedule = updatedNurseSchedule.filter(
				nurse => nurse.nurseId !== editedNurseId
			)

			for (const patient of updatedNursePatients) {
				patient.assignedChair = null
				patient.assignedNurse = null
			}
		}

		// Dispatch updates
		if (isTomorrowMode) {
			dispatch(
				updateTomorrowSchedule({
					patientSchedule: updatedPatientSchedule,
					nurseSchedule: updatedNurseSchedule,
					chairSchedule: updatedChairSchedule
				})
			)
			if (completelyRemoveNurse) {
				dispatch(removeTomorrowNurse(editedNurseId))
			}
		} else if (sandbox) {
			dispatch(
				updateSandboxSchedule({
					patientSchedule: updatedPatientSchedule,
					nurseSchedule: updatedNurseSchedule,
					chairSchedule: updatedChairSchedule
				})
			)
			if (completelyRemoveNurse) {
				dispatch(removeSandboxNurse(editedNurseId))
			}
		} else {
			dispatch(
				updateSchedule({
					patientSchedule: updatedPatientSchedule,
					nurseSchedule: updatedNurseSchedule,
					chairSchedule: updatedChairSchedule
				})
			)
			if (completelyRemoveNurse) {
				dispatch(removeNurse(editedNurseId))
			}
		}

		if (isTomorrowMode) {
			dispatch(markNurseAsEditedTomorrow({ nurseId: editedNurseId, editedProperties }))
		} else {
			dispatch(markNurseAsEdited({ nurseId: editedNurseId, editedProperties }))
		}
	}

	const confirmRemoveNurseClicked = () => {
		setIsDialogOpen(false)
		editNurseInfo()
		confirmRemoveNurse()
	}

	const confirmRemoveNurse = async () => {
		setNotification({ visible: true, message: "Removed Nurse!", success: false })
		setTimeout(() => setNotification({ visible: false, message: "" }), 3000)

		try {
			await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await schedulePatch.mutateAsync({ date, setNotification })
		} catch (error) {
			logging.error("RemoveNursePopup - error executing schedule update:", {
				error: error.message,
				stack: error.stack
			})
		}
		dispatch(setIsEditing(false))
	}

	const handleTransitionAccept = async () => {
		logging.info("RemoveNursePopup - reassignments accepted", {
			component: "RemoveNursePopup",
			function: "handleTransitionAccept",
			nurseToRemove: selectedNurse,
			reassignments: reassignments
		})
		setIsTransitionAcceptOpen(false)

		if (!selectedNurse) return

		try {
			if (sandbox) {
				dispatch(transferPatientsBetweenNursesAndChairsSandbox(reassignments))
			} else if (isTomorrowMode) {
				dispatch(transferPatientsBetweenNursesAndChairsTomorrow(reassignments))
			} else {
				dispatch(transferPatientsBetweenNursesAndChairs(reassignments))
			}

			setEditedProperties(editedProperties => [...editedProperties, "assignedPatients"])
			editNurseInfo()
			confirmRemoveNurse()
		} catch (error) {
			logging.error("RemoveNursePopup - error executing reassignment:", {
				error: error.message,
				stack: error.stack
			})
		}

		setTimeout(() => setNotification({ visible: false, message: "" }), 2000)
	}

	const handleTransitionDecline = () => {
		logging.info("RemoveNursePopup - reassignments declined", {
			component: "RemoveNursePopup",
			function: "handleTransitionDecline",
			nurseToRemove: selectedNurse,
			reassignments: reassignments
		})
		dispatch(setIsEditing(false))
		setNotification({
			visible: true,
			message:
				"You cannot remove a nurse that has patients assigned. Please reassign patients first.",
			success: false
		})
		setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		setIsTransitionAcceptOpen(false)
	}

	const filterNursesToRemove = () => {
		let currentState
		if (isTomorrowMode) {
			currentState = store.getState().tomorrow
		} else if (sandbox) {
			currentState = store.getState().sandbox // Assuming you have a sandbox state
		} else {
			currentState = store.getState().schedule
		}

		const { nurseSchedule: currentNurseSchedule } = currentState

		// Sort nurses by name
		return currentNurseSchedule
			.filter(
				nurse =>
					currentlyNurseRealTimeOngoing(nurse, sandbox, isTomorrowMode) ||
					currentlyNurseRealTimeFuture(nurse, sandbox, isTomorrowMode)
			)
			.sort((a, b) => a?.nurseName.localeCompare(b?.nurseName)) // Sort by nurse name
	}

	const scrollableRef = useRef(null)

	useEffect(() => {
		// Set scroll to top whenever the component is mounted
		if (scrollableRef.current) {
			scrollableRef.current.scrollTop = 0
		}
	}, []) // Empty dependency ensures it runs only on mount

	return (
		<>
			<Popup
				trigger={
					<button className="h-9 bg-gray-100 text-dark text-sm font-semibold px-4 m-1 rounded flex items-center">
						<TiCancel className="mr-2 w-4 h-4" /> Remove Nurse
					</button>
				}
				modal
				nested
				contentStyle={{ width: "auto", border: "none", borderRadius: "10px", padding: "0" }}
				overlayStyle={{ background: "rgba(0, 0, 0, 0.5)" }}
				onOpen={handlePopupOpen}
				onClose={handleClose}
			>
				{close => (
					<div
						className="bg-white p-6 rounded-2xl shadow-lg w-96 mx-auto max-h-96 overflow-y-auto"
						ref={scrollableRef}
					>
						<div className="flex items-center text-xl font-semibold mb-6 text-[#091556]">
							<FaUserMinus className="w-5 h-5 text-[#560909] mr-2" />
							Select Nurse to Remove
						</div>
						<div className="mb-4">
							{filterNursesToRemove().map(nurse => (
								<div
									key={nurse.nurseId}
									className="cursor-pointer p-2 flex items-center hover:bg-[#F3F4F6] rounded-md"
									onClick={() => {
										handleRemoveNurse(nurse)
										close()
									}}
								>
									<div className="flex items-center justify-center w-7 h-7 bg-[#959DC5] text-white font-semibold rounded-full text-xs mr-3">
										{nurse.nurseName
											.split(" ")
											.map(n => n[0])
											.join("")}
									</div>
									<span className="text-[#091556] font-mum">
										{nurse.nurseName}
									</span>
								</div>
							))}
						</div>
						<div className="flex justify-end">
							<button
								onClick={close}
								className="flex items-center gap-2 bg-[#091556] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1a2570] transition ring-2 ring-offset-2 ring-[#091556]/30"
							>
								<IoClose className="w-4 h-4" />
								Close
							</button>
						</div>
					</div>
				)}
			</Popup>

			{/* Show the TransitionAccept component */}
			<PatientReassignmentConfirmation
				title="Patient Reassignments Required to Remove Nurse"
				message={`Before you can remove this nurse, please ensure all active patient appointments are reassigned. Below are the suggested reassignments based on current availability:`}
				isOpen={isTransitionAcceptOpen}
				onAccept={handleTransitionAccept}
				onDecline={handleTransitionDecline}
				reassignments={reassignments}
				scheduleSelection={scheduleSelection}
				remove={true}
			/>

			{/* Confirmation dialog for final nurse removal */}
			<ConfirmationDialog
				title="Are you sure?"
				message={`Do you really want to remove nurse ${
					selectedNurse ? selectedNurse.nurseName : ""
				}? This action cannot be undone.`}
				isOpen={isDialogOpen}
				onConfirm={confirmRemoveNurseClicked}
				onCancel={() => {
					setIsDialogOpen(false)
					handleClose()
				}}
			/>
		</>
	)
}

export default RemoveNursePopup
